import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

import { ImgixNextImage } from "@components/imgixNextImage";
import { DESTINATION_PROP_TYPES } from "@constants/places/proptypes";
import { Breadcrumbs } from "@components/breadcrumbs";
import { Driver } from "@components/_pages/destinations/intro/driver";
import { tagular } from "@cohesion/tagular";

export function Hero({ featuredImage, breadcrumb, book, ctx }) {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      tagular("product_view", {
        product: {
          location: "DESTINATION HERO",
          text: "SHOP BOOK",
          position: 1,
          brand: "shop",
        },
      });
    }
  }, [inView]);
  return (
    <section className="relative w-full lg:bg-blue-100 overflow-x-hidden">
      <div className="grid-container md:grid md:grid-cols-[1fr_minmax(auto,_calc((var(--container-width))/3))_minmax(auto,_calc((var(--container-width)/3*2)))_1fr]">
        <div className="bg-blue-100 px-4 py-6 2xl:pl-0 lg:p-12 lg:flex lg:flex-col lg:justify-start md:col-start-1 md:col-span-2 xl:col-start-2 xl:col-span-1">
          {breadcrumb && (
            <div className="text-md">
              <Breadcrumbs useCarat breadcrumbs={breadcrumb} />
            </div>
          )}
          <h1 className="lg:inline heading-02 text-blue lg:mb-auto">
            {ctx.title}
          </h1>

          {book && (
            <article
              ref={ref}
              className="rounded-md border border-black-100 bg-white p-4 gap-x-4 flex items-center lg:flex-col lg:mb-0 mt-12 lg:mt-24 w-fit lg:w-full max-w-full xl:max-w-xs"
            >
              <div className="flex lg:w-full shrink-0 lg:mb-6">
                <ImgixNextImage
                  className="w-auto mx-auto object-contain max-h-[170px] lg:w-full lg:max-h-[300px]"
                  src={book.images?.[0]?.src}
                  loader={uri => `${uri.src}&width=285`}
                  alt=""
                  width={110}
                  height={170}
                  imgixParams={{ w: 110 }}
                />
              </div>
              <div className="w-full flex flex-col flex-start lg:text-center lg:mt-auto">
                <p className="uppercase label-sm">Shop</p>
                <p className="mb-auto lg:hidden">
                  Get even more with a Lonely Planet guide.
                </p>
                <p className="mb-auto text-xl font-semibold hidden lg:block">
                  {book.title.split("-")[0]}
                </p>
                <p className="hidden lg:block">
                  ${book.variants[0].price.amount}
                </p>
                <a
                  href={`${book.url}?utm_source=lonelyplanet&utm_campaign=hubsherodriver&utm_content=${ctx.title}`}
                  className="btn btn-primary mt-4 mb-0 lg:text-center w-fit lg:mx-auto"
                  target="_neue"
                  rel="noopener noreferrer"
                  onClick={() =>
                    tagular("product_click", {
                      actionOutcome: "INTERNALLINK",
                      correlationId: "",
                      product: {
                        location: "DESTINATION HERO",
                        text: "SHOP BOOK",
                        name: book.title,
                        position: 1,
                        brand: "shop",
                        productId: book.variants[0].id,
                        sku: book.variants[0].sku,
                        price: Number(book.variants[0].price.amount),
                      },
                      outboundUrl: book.url,
                    })
                  }
                >
                  Shop Book
                </a>
              </div>
            </article>
          )}
        </div>
        <div className="relative md:flex md:col-span-2 md:col-start-3">
          <ImgixNextImage
            className="lg:w-full lg:h-full lg:object-cover"
            src={featuredImage?.url}
            alt={featuredImage?.alt}
            width={1095}
            height={742}
            sizes="(max-width: 1024px) 75vw, 100vw"
            imgixParams={{ w: 1095, fit: "crop", crop: "faces,edges" }}
            priority
          />
          <div className="absolute top-0 w-full h-full bg-gradient-to-b from-black/40 via-black/10 to-transparent" />

          {featuredImage?.credit && (
            <p className="absolute left-6 bottom-2 transform -translate-y-4 w-content p-1 m-0 bg-black bg-opacity-80 text-white text-xs leading-tight rounded-sm">
              {featuredImage.credit}
            </p>
          )}
        </div>
        <div className="md:col-start-1 md:col-span-4 md:mx-auto lg:absolute lg:right-7 lg:bottom-7 rounded-md w-[calc(100%-2rem)] lg:max-w-[410px] ml-4 my-4">
          <Driver ctx={ctx} />
        </div>
      </div>
      <style jsx>{`
        .grid-container {
          --container-width: 768px;
          @media (min-width: 1024px) {
            --container-width: 1024px;
          }
          @media (min-width: 1280px) {
            --container-width: 1280px;
          }
          @media (min-width: 1536px) {
            --container-width: 1536px;
          }
        }
      `}</style>
    </section>
  );
}

Hero.propTypes = {
  breadcrumb: DESTINATION_PROP_TYPES.breadcrumb, // eslint-disable-line react/require-default-props
  featuredImage: DESTINATION_PROP_TYPES.featuredImage, // eslint-disable-line react/require-default-props
  ctx: DESTINATION_PROP_TYPES.ctx, // eslint-disable-line react/require-default-props
  book: DESTINATION_PROP_TYPES.book, // eslint-disable-line react/require-default-props
};
